import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { formatElapsed, useElapsed } from '../lib/elapsed';
import { onTimerChange, timerChanged } from '../lib/timer';
import type { RunningTimer, WorkItemTime } from '../lib/types';
import { formatMinutes } from '../lib/types';

/**
 * Time booked against this task, and the timer for it.
 *
 * THE TIMER USED TO LIVE ONLY ON THE TIMESHEET PAGE. Starting one meant leaving whatever you
 * were doing, going to Timesheets, choosing the project from a list, choosing the task from a
 * second list, and pressing start — five steps to say "I am working on this now", at the exact
 * moment somebody has just decided to start working. So people did not, and typed the hours in
 * on Friday from memory instead, which is where timesheets go wrong.
 *
 * Here it is one button on the task itself, reachable from all four board views.
 *
 * The panel no longer hides when nothing has been logged. That was right when it could only
 * REPORT time — an empty card says nothing — and it is wrong now that it is also how you start
 * one: a task with no time on it is exactly the task you are about to start.
 */
export function TaskTimePanel({
  itemId,
  projectId,
  itemKey,
  canLog,
  billable,
  billing,
}: {
  itemId: string;
  projectId: string;
  itemKey: string;
  /** `time.log`. A customer holds none of it and is shown the total without the button. */
  canLog: boolean;
  /** Whether hours here are chargeable — the task's own setting, or its project's when it has none. */
  billable: boolean;
  /**
   * The choice itself, for whoever may set it: the task's own setting (null = as the project
   * is), the project's, and how to change it. Absent for everybody else, who see the hint only.
   */
  billing?: { task: boolean | null; project: boolean; saving: boolean; onChange: (value: boolean | null) => void };
}) {
  const [time, setTime] = useState<WorkItemTime | null>(null);
  const [timer, setTimer] = useState<RunningTimer | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [booked, running] = await Promise.all([
      api.get<WorkItemTime>(`/work-items/${itemId}/time`).catch(() => null),
      canLog ? api.get<RunningTimer | null>('/time/timer').catch(() => null) : Promise.resolve(null),
    ]);
    setTime(booked);
    setTimer(running);
  }, [itemId, canLog]);

  useEffect(() => {
    void load();
    /* The shell's chip and the timesheet can stop this timer too. See `lib/timer`. */
    return onTimerChange(() => void load());
  }, [load]);

  async function act(fn: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
      /* So the shell's chip agrees at once rather than up to a minute later. */
      timerChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change the timer');
    } finally {
      setBusy(false);
    }
  }

  const here = timer?.workItem?.id === itemId;
  /**
   * COUNTED HERE, NOT TAKEN FROM THE API. `elapsedMinutes` is worked out when the server
   * answers, so re-rendering redraws the same number — it sat at "0m" and never moved, which
   * reads as a broken clock rather than a running one. Same hook the shell's chip uses, so the
   * two cannot disagree about the same timer.
   */
  const elapsed = useElapsed(here ? timer?.startedAt : null);
  /** Running, but on something else — the case that used to mean a trip to the timesheet. */
  const elsewhere = timer && !here;

  if (!time && !canLog && !billing) return null;

  const total = time?.totalMinutes ?? 0;
  const estimate = time?.estimateMinutes ?? null;
  const over = estimate !== null && total > estimate;
  /* The running minutes are not booked yet, so they are shown beside the total, not inside it. */

  return (
    <div className="card mt-2">
      <div className="card-head">
        <h3>Time</h3>
        <span className="mono" style={over ? { color: 'var(--accent)' } : undefined}>
          {formatMinutes(total)}
          {estimate !== null ? ` of ${formatMinutes(estimate)}` : ''}
        </span>
      </div>

      {/*
        BILLABLE, beside the timer it decides for. A bug fixed under warranty on a billable
        project, a requested task on one that is otherwise free. Hours booked on it from now
        take this; hours already booked and not yet invoiced follow it too.
      */}
      {billing && (
        <div className="card-pad" style={{ borderBottom: '1px solid var(--line)' }}>
          <label className="row" style={{ gap: 10, alignItems: 'center' }}>
            <span className="field-label" style={{ margin: 0, flex: 'none' }}>
              Billable
            </span>
            <select
              value={billing.task == null ? '' : billing.task ? 'yes' : 'no'}
              disabled={billing.saving}
              onChange={(event) => billing.onChange(event.target.value === '' ? null : event.target.value === 'yes')}
              style={{ flex: 1, minWidth: 0 }}
            >
              <option value="">As the project is — {billing.project ? 'billable' : 'not billable'}</option>
              <option value="yes">Billable</option>
              <option value="no">Not billable</option>
            </select>
          </label>
          <span className="field-hint">
            Time booked on this follows it. Hours already booked follow too, unless they have been invoiced.
          </span>
        </div>
      )}

      {canLog && (
        <div className="card-pad">
          {error && <div className="callout mb-2">{error}</div>}

          {here ? (
            <div className="row-between">
              <span className="row" style={{ gap: 8 }}>
                <span className="dot-pulse" aria-hidden />
                <span>
                  Running · <span className="mono running-timer-clock">{formatElapsed(elapsed)}</span>
                </span>
              </span>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={busy}
                onClick={() => void act(() => api.post('/time/timer/stop'))}
              >
                {busy ? 'Stopping…' : 'Stop timer'}
              </button>
            </div>
          ) : elsewhere ? (
            <div className="row-between" style={{ gap: 12 }}>
              <span className="dim" style={{ fontSize: 13 }}>
                A timer is running on{' '}
                <span className="mono">{timer.workItem ? timer.workItem.key : timer.projectName}</span>.
              </span>
              {/*
                SWITCHING, rather than the API's refusal repeated on screen.

                One timer per person is the right rule, but "stop that one first" sends somebody
                back to the page this panel exists to avoid. Two calls with a gap between them:
                if the start fails, the stop has still happened and is visible here, which is
                recoverable — where starting first and failing to stop would double-count.
              */}
              <button
                type="button"
                className="btn btn-sm"
                disabled={busy}
                onClick={() =>
                  void act(async () => {
                    await api.post('/time/timer/stop');
                    await api.post('/time/timer/start', { projectId, workItemId: itemId });
                  })
                }
              >
                {busy ? 'Switching…' : 'Switch to this task'}
              </button>
            </div>
          ) : (
            <div className="row-between">
              {/*
                SAYING WHICH WAY IT WILL BOOK, because the task — or, when the task does not say,
                its project — decides it, and neither is in front of whoever starts the timer.
                Non-billable hours booking as non-billable is correct and silent, and silent is how
                it is discovered a month later on a short invoice.
              */}
              <span className="dim" style={{ fontSize: 13 }}>
                Nothing running. It books to <span className="mono">{itemKey}</span>
                {billable ? ' as billable.' : ' as non-billable.'}
              </span>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={busy}
                onClick={() => void act(() => api.post('/time/timer/start', { projectId, workItemId: itemId }))}
              >
                {busy ? 'Starting…' : 'Start timer'}
              </button>
            </div>
          )}
        </div>
      )}

      {time && time.entries.length > 0 && (
        <div className="card-pad stack-sm" style={{ borderTop: '1px solid var(--line)' }}>
          {time.entries.map((entry) => (
            <div key={entry.id} className="row-between" style={{ fontSize: 13 }}>
              <span className="truncate" style={{ maxWidth: 300 }}>
                <span className="mono dim">{entry.workDate}</span> {entry.person}
                {entry.description ? ` — ${entry.description}` : ''}
              </span>
              <span className="mono">{formatMinutes(entry.minutes)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
